// src/public-routes/sectors/SaaSSector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Progress, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaCloud,
    FaSearch,
    FaRocket,
    FaSync,
    FaUsers,
    FaChartLine,
    FaCogs,
    FaTrophy
} from 'react-icons/fa';

const SaaSSector = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingPercent, setLoadingPercent] = useState(0);
    const loadingRef = useRef(null);

    // Handle percentage-based loading
    useEffect(() => {
        window.scrollTo(0, 0);
        if (!isLoading) return;

        const interval = setInterval(() => {
            setLoadingPercent(prev => {
                const newPercent = Math.min(prev + 5, 100);

                if (newPercent === 100) {
                    clearInterval(interval);
                    setTimeout(() => {
                        setIsLoading(false);
                        window.scrollTo(0, 0);
                    }, 500);
                }

                return newPercent;
            });
        }, 100);

        return () => clearInterval(interval);
    }, [isLoading]);

    const stats = [
        { number: '$307B', label: 'Global SaaS Market Value by 2026' },
        { number: '85%', label: 'of business apps will be SaaS-based by 2025' },
        { number: '5x', label: 'higher customer LTV with full-funnel optimization' },
        { number: '67%', label: 'reduction in churn with lifecycle marketing' }
    ];

    const approaches = [
        {
            title: "User Acquisition",
            description: "Scale your user base through targeted paid media, content marketing, and SEO strategies that attract qualified prospects at every stage of the funnel.",
            icon: FaRocket
        },
        {
            title: "Conversion Optimization",
            description: "Turn free trials into paying customers with optimized onboarding flows, landing pages, and conversion rate optimization strategies.",
            icon: FaSync
        },
        {
            title: "Customer Retention",
            description: "Reduce churn and increase lifetime value through engagement marketing, personalized communication, and proactive customer success campaigns.",
            icon: FaUsers
        },
        {
            title: "Lifecycle Marketing",
            description: "Build automated marketing systems that nurture users from signup to power user, maximizing expansion revenue and referrals.",
            icon: FaCogs
        }
    ];

    const benefits = [
        ['Accelerated User Growth', 'Scale your user acquisition engine with predictable, measurable channels'],
        ['Improved Conversion Rates', 'Convert more free users into paying customers with optimized funnels'],
        ['Reduced Churn Rate', 'Keep customers engaged and subscribed longer with retention strategies'],
        ['Higher Customer LTV', 'Maximize lifetime value through upsells, cross-sells, and expansion revenue'],
        ['Data-Driven Decisions', 'Make informed growth decisions with advanced analytics and attribution']
    ];

    const questions = [
        'How can you scale user acquisition while maintaining CAC efficiency?',
        'What strategies will convert more free trials into paid subscriptions?',
        'How do you reduce churn and increase customer lifetime value?',
        'Which channels deliver the highest quality users for your SaaS?',
        'How can you optimize your full funnel from awareness to advocacy?'
    ];

    return (
        <>
            <div className="st-page">
                {/* Top Image Section */}
                <div className="st-decorative-image-container">
                    <img
                        src={require('./SaaS.PNG')}
                        alt="SaaS Growth Marketing - Scale Your Platform"
                        loading="eager"
                    />
                </div>

                {/* Title Section */}
                <div className="sv-page-title-section">
                    <div className="container">
                        <h1>
                            <span className="sv-gradient-text">SaaS</span> Growth Marketing
                        </h1>
                        <p>Accelerate growth through scalable acquisition strategies, performance marketing, and lifecycle optimisation</p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container-fluid sv-content-wrapper">
                    <div className="sv-main-content">
                        {/* Hero Section */}
                        <section className="sv-hero-section" data-animated>
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <p className="sv-lead">
                                        From early-stage startups to established <span className="sv-keyword-highlight">SaaS platforms</span>, we help accelerate growth through scalable acquisition strategies, performance marketing, and lifecycle optimisation.
                                    </p>
                                    <p className="sv-description">
                                        At Reachtix, our <strong>SaaS growth marketing services</strong> combine strategic thinking, data intelligence, and advanced tools to create campaigns that drive user acquisition, improve conversion rates, and maximize customer lifetime value. We understand the unique metrics that matter for SaaS businesses.
                                    </p>
                                    <p className="sv-description">
                                        We understand the unique challenges SaaS companies face: balancing growth with unit economics, reducing churn, optimizing trial-to-paid conversion, and building scalable acquisition channels that deliver predictable MRR growth.
                                    </p>
                                    <div className="sv-cta-buttons">
                                        <Button tag={Link} to="/contact" color="dark">
                                            <i className="fas fa-rocket mr-2"></i>
                                            Start SaaS Growth Strategy
                                        </Button>
                                        <Button tag={Link} to="/contact" color="dark">
                                            <i className="fas fa-comments mr-2"></i>
                                            Get Free Consultation
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Stats Section */}
                        <section className="sv-stats-section">
                            <h2 className="sv-section-title" data-animated>
                                Why <span className="sv-text-gradient">SaaS Marketing</span> Matters
                            </h2>
                            <Row className="sv-stats-grid">
                                {stats.map((stat, index) => (
                                    <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                        <div className="sv-stat-card">
                                            <div className="sv-stat-number">{stat.number}</div>
                                            <div className="sv-stat-label">{stat.label}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </section>

                        {/* What We Do Section */}
                        <section className="sv-explanation-section" data-animated>
                            <Row>
                                <Col lg={8}>
                                    <h2 className="sv-section-title">What We Do for SaaS Companies</h2>
                                    <p className="sv-section-description">
                                        <strong>SaaS growth marketing</strong> goes beyond just driving signups. It's about creating a predictable, scalable growth engine that acquires qualified users, converts them into paying customers, and retains them for the long term while maximizing lifetime value.
                                    </p>
                                    <div className="sv-questions-grid">
                                        <h4>Key Questions We Answer:</h4>
                                        {questions.map((question, index) => (
                                            <div key={index} className="sv-question-item">
                                                <i className="fas fa-question-circle"></i>
                                                {question}
                                            </div>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Benefits Overview */}
                        <section className="sv-benefits-overview" data-animated>
                            <Row>
                                <Col xl={12}>
                                    <h2 className="sv-section-title">Why Invest in SaaS Marketing?</h2>
                                    <p className="sv-section-description">
                                        The SaaS landscape is more competitive than ever. A comprehensive growth marketing strategy is essential to stand out in crowded categories, acquire users efficiently, and build a sustainable subscription business that grows predictably.
                                    </p>
                                    <div className="sv-benefits-cards">
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-users"></i>
                                            <h5>Scalable Acquisition</h5>
                                            <p>Build predictable user acquisition channels that scale with your growth</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-sync"></i>
                                            <h5>Better Conversion</h5>
                                            <p>Optimize trial-to-paid conversion with data-driven funnel improvements</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-heart"></i>
                                            <h5>Reduced Churn</h5>
                                            <p>Keep customers engaged and subscribed with proactive retention strategies</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-chart-line"></i>
                                            <h5>MRR Growth</h5>
                                            <p>Drive predictable monthly recurring revenue through full-funnel optimization</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Our Approach Section */}
                        <section className="sv-approach-section" data-animated>
                            <Row>
                                <Col>
                                    <h2 className="sv-section-title">Our SaaS Marketing Services</h2>
                                    <p className="sv-section-description">
                                        Reachtix offers a comprehensive suite of SaaS growth marketing services designed to accelerate your growth at every stage — from early-stage user acquisition to scaling and retention optimization.
                                    </p>

                                    <Row className="sv-services-grid">
                                        {approaches.map((service, index) => (
                                            <Col lg={6} md={6} key={index} className="mb-4">
                                                <div className="sv-approach-card p-4 h-100">
                                                    <div className="sv-approach-icon">
                                                        <service.icon />
                                                    </div>
                                                    <div className="sv-approach-content">
                                                        <h4>{service.title}</h4>
                                                        <p className="service-description">{service.description}</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Col>
                            </Row>
                        </section>

                        {/* Benefits Table Section */}
                        <section className="sv-benefits-section" data-animated>
                            <Row>
                                <Col>
                                    <h2 className="mt-5 sv-section-title">Key Benefits of SaaS Marketing With Reachtix</h2>
                                    <div className="table-responsive">
                                        <table className="table sv-benefits-table">
                                            <thead>
                                                <tr>
                                                    <th>Core Advantage</th>
                                                    <th>Business Impact</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {benefits.map(([advantage, impact], index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <i className="fas fa-check-circle"></i>
                                                            <strong>{advantage}</strong>
                                                        </td>
                                                        <td>{impact}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Main CTA Section */}
                        <section className="sv-main-cta" data-animated>
                            <Row>
                                <Col>
                                    <h2>Ready to Accelerate Your SaaS Growth?</h2>
                                    <p className="sv-cta-description">
                                        Whether you're an early-stage startup seeking product-market fit or an established platform scaling to new heights, our comprehensive SaaS marketing services provide the expertise and results you need to grow predictably.
                                    </p>
                                    <p className="sv-cta-description">
                                        Let's build a growth engine that drives user acquisition, improves retention, and maximizes customer lifetime value.
                                    </p>
                                    <div className="sv-cta-buttons">
                                        <Button tag={Link} to="/contact" color="primary">
                                            <i className="fas fa-calendar-check mr-2"></i>
                                            Start SaaS Growth Consultation
                                        </Button>
                                        <Button tag={Link} to="/contact" color="light">
                                            <i className="fas fa-clipboard-list mr-2"></i>
                                            Get Growth Audit
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SaaSSector;